import getSymbolFromCurrency from 'currency-symbol-map';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    convertISOToRelativeDate,
    convertMetersIntoKilometers,
    convertSecondsIntoHHmmDisplay,
} from '../../common/utils';
import { DefaultPanel } from '../../components/Panel/DefaultPanel';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { trace } from '../../configuration/setup/trace';
import { resolveAddress } from '../../features/fetchData/resolveAddress';
import { TimelineEventType } from '../../store/facade/facadeApiV3';
import { supportedLocaleMap } from '../../store/lang/lang';
import { selectLocale } from '../../store/lang/langSelectors';
import { selectVehicleCosts } from '../../store/route/routeSelectors';
import { selectCurrentTour } from '../../store/tour/tourSelectors';
import type { Itinerary } from '../../store/tour/tourSlice';
import { TourSummaryPanelBody } from './TourSummaryPanelBody';
import { TourSummaryPanelFooter } from './TourSummaryPanelFooter';
import { TourSummaryPanelHeader } from './TourSummaryPanelHeader';

export type TourSummaryProps = {
    itineraries: Itinerary[];
    selectedRoute: number;
};

export const TourSummary = ({ itineraries, selectedRoute }: TourSummaryProps) => {
    const dispatch = useAppDispatch();

    const [vehicleAddress, setVehicleAddress] = useState<string>();

    const userLocale = useAppSelector(selectLocale);
    const vehicleCosts = useAppSelector(selectVehicleCosts);
    const tour = useAppSelector(selectCurrentTour);

    const localLang = (Object.keys(supportedLocaleMap) as (keyof typeof supportedLocaleMap)[]).find(key => {
        return key !== userLocale && supportedLocaleMap[key] === userLocale;
    }) as string;

    const itinerary = itineraries.at(selectedRoute);
    const routeSummary = itinerary?.summary;

    const tourDepartureTime = itinerary?.sections?.at(0)?.departureTime;
    const tourArrivalTime = itinerary?.sections?.at(-1)?.arrivalTime;

    const departureEvent = itinerary?.events?.timelineEvents?.find(
        e => e.timelineEventType === TimelineEventType.Departure
    );
    const arrivalEvent = itinerary?.events?.timelineEvents?.find(
        e => e.timelineEventType === TimelineEventType.Arrival
    );

    const isFirstWpVehicleLocation =
        departureEvent?.timelineEventType === TimelineEventType.Departure &&
        !departureEvent.address?.name &&
        !departureEvent.address?.street;

    const departureAddress =
        departureEvent?.timelineEventType === TimelineEventType.Departure
            ? (departureEvent.address?.name ?? departureEvent.address?.street)
            : undefined;

    const arrivalAddress =
        arrivalEvent?.timelineEventType === TimelineEventType.Arrival
            ? (arrivalEvent.address?.name ?? arrivalEvent.address?.street)
            : undefined;

    const departureCoordinates =
        departureEvent?.timelineEventType === TimelineEventType.Departure ? departureEvent.coordinates : undefined;

    const getVehicleLocation = async () => {
        try {
            const eventObj = {
                type: 'EVENT_SMART_ROUTING',
                payload: { coordinates: departureCoordinates },
            };
            const resolvedAddress = await dispatch(resolveAddress(eventObj));
            setVehicleAddress(resolvedAddress);
        } catch (error) {
            trace.error('Could not resolve vehicle address: ', error);
        }
    };

    useEffect(() => {
        if (isFirstWpVehicleLocation) {
            getVehicleLocation();
        }
    });

    const getTourSummaryPanelHeader = () => {
        return (
            <TourSummaryPanelHeader
                headerDuration={convertSecondsIntoHHmmDisplay(itinerary?.summary?.duration)}
                headerDistance={convertMetersIntoKilometers(itinerary?.summary?.distance)?.toString()}
            />
        );
    };

    const getTourSummaryPanelBody = () => {
        return (
            <TourSummaryPanelBody
                bodyDepTime={convertISOToRelativeDate(localLang, tourDepartureTime)}
                bodyDepAddress={isFirstWpVehicleLocation ? vehicleAddress : departureAddress}
                bodyArrTime={convertISOToRelativeDate(localLang, tourArrivalTime)}
                bodyArrAddress={arrivalAddress}
                totalConsumption={routeSummary?.evConsumption}
                vehicleCost={routeSummary?.vehicleCost}
                energyCost={routeSummary?.energyCost}
                tollCostMin={routeSummary?.minTollCost}
                tollCostMax={routeSummary?.maxTollCost}
                totalCostMin={routeSummary?.minTotalCost}
                totalCostMax={routeSummary?.maxTotalCost}
                currency={getSymbolFromCurrency(vehicleCosts.currency)}
            />
        );
    };

    const getTourSummaryPanelFooter = () => {
        return <TourSummaryPanelFooter footerTimestamp={convertISOToRelativeDate(localLang, tour?.lastUpdate)} />;
    };

    return (
        <div data-testid='tour-summary-panel'>
            <div className='text-size-18 text-medium margin-bottom-10' data-testid='tour-alternatives-label'>
                <FormattedMessage id='intl-msg:smartRoute.tour.summary' />
            </div>
            <DefaultPanel
                panelHeader={getTourSummaryPanelHeader()}
                panelBody={getTourSummaryPanelBody()}
                panelFooter={getTourSummaryPanelFooter()}
            />
        </div>
    );
};
